
import React from "react";
import { motion } from "framer-motion";
import StatsCard from "./StatsCard";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5 }
  }
};

interface StatsData {
  totalStudents: number;
  totalPrograms: number;
  totalRevenue: number;
  newStudents: number;
  totalTeachers?: number;
  revenuePeriods?: {
    monthly: number;
    annual: number;
  };
  pendingAmount?: number;
  partialAmount?: number;
}

interface StatsSectionProps {
  stats: StatsData;
  loading: boolean;
}

const StatsSection = ({ stats, loading }: StatsSectionProps) => {
  return (
    <motion.div 
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <StatsCard
          label="Total Students"
          value={stats.totalStudents}
          description="All registered students"
          icon="Users"
          loading={loading}
          navigateTo="/total-students"
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <StatsCard
          label="Programs"
          value={stats.totalPrograms}
          description="Active program enrollments"
          icon="BookOpen"
          loading={loading}
          navigateTo="/programs/list"
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <StatsCard
          label="Revenue"
          value={stats.totalRevenue}
          description={stats.pendingAmount && stats.pendingAmount > 0 ? 
            `${stats.pendingAmount} AFN pending` : 
            "Total program fees"}
          icon="DollarSign"
          loading={loading}
          navigateTo="/revenue"
          periodData={stats.revenuePeriods}
          statusData={{
            pending: stats.pendingAmount || 0,
            partial: stats.partialAmount || 0
          }}
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <StatsCard
          label="New Students"
          value={stats.newStudents}
          description="Added in the last 30 days"
          icon="UserPlus"
          loading={loading}
          navigateTo="/students/new-list"
        />
      </motion.div>
      
      <motion.div variants={itemVariants}>
        <StatsCard
          label="Teachers"
          value={stats.totalTeachers || 0}
          description="All active teachers"
          icon="GraduationCap"
          loading={loading}
          navigateTo="/teachers/overview"
        />
      </motion.div>
    </motion.div>
  );
};

export default StatsSection;
